import { SafeHtml } from "@angular/platform-browser";
import {
  DefaultTemplateType,
  DigitalAssetGetDto,
  DigitalAssetScreenSize,
  GlobalTemplateType,
  HtmlTemplateParameters,
  KeyValueAsset,
  MediaContentDto,
  TemplateConfigurationTriggerActionType,
  TemplateConfigurationTriggerAreaType,
  TemplateConfigurationTriggerType,
  TemplateContentType,
  TemplateDestinationType,
  TemplateImageSourceType,
  TemplateLogoConfigurationDto
} from "clearline-api";
import { EventEmitter } from "@angular/core";

export interface Template {
  id?: number;
  templateContentType?: TemplateContentType;
  templateContentTypeId?: number;
  contentSource?: TemplateContentSource;
  templateId: number;
  name: string;
  templateName?: string; // default template name
  widgetId?: number;
  defaultTemplateId?: number;
  templateType?: DefaultTemplateType;
  globalTemplateType: GlobalTemplateType;
  isDefault?: boolean;
  canEnable?: boolean;
  enabled: boolean;
  templateProperties?: TemplateProperties;
  configuration?: TemplateConfigurationDto;
  generatedBody?: string | SafeHtml;
  previewOptions?: TemplatePreviewOptions;
  redirectSettings?: TemplateInteractionRedirectConfigModel;
  isSplashScreen?: boolean;
  createdOn?: string;
}

export interface TemplateInteractionRedirectConfigModel {
  redirectPageUrl?: string;
  timeoutInSeconds: number;
}

export interface TemplateConfigurationDto {
  mediaContentId?: number;
  templateContentType?: TemplateContentType;
  options?: TemplateConfigurationOptionsDto;
  triggers?: TemplateConfigurationTriggerDto[];
  parameters?: HtmlTemplateParameters;
}

export interface TemplateConfigurationOptionsDto {
  defaultDestination?: TemplateDestinationDto;
  displayTime?: number; // in ms
  image: TemplateImageConfigurationDto;
  logo: TemplateLogoConfigurationDto;
}

export interface TemplateImageConfigurationDto {
  sourceType: TemplateImageSourceType;
}

export interface TemplateConfigurationTriggerDto {
  triggerType: TemplateConfigurationTriggerType;
  area: TemplateConfigurationTriggerAreaDto;
  action: TemplateConfigurationTriggerActionDto;
}

export interface TemplateConfigurationTriggerAreaDto {
  areaType: TemplateConfigurationTriggerAreaType;
}

export interface TemplateConfigurationTriggerActionDto {
  actionType: TemplateConfigurationTriggerActionType;
  destination: TemplateCustomDestinationDto;
}

export interface TemplateDestinationDto {
  destinationType: TemplateDestinationType;
  url?: string;
  widgetType?: number;
}

export interface TemplateCustomDestinationDto {
  useDefaultDestination: boolean;
  customDestination?: TemplateDestinationDto;
}

export interface ContentSettings {
  assetId?: number;
  isPredefined: boolean;
  templateCategory: string;
  displayContent: string;
  backgroundColor: string;
  buttonColor: string;
  campaignSchedule: string;
}

export interface UrlContentSettings {
  ContentType: CustomContentTemplateType;
  VideoContentType?: VideoTemplateContentType;
  ImageUrl: string;
  VideoUrl?: string;
  ContentDuration?: number;
}

export enum VideoTemplateContentType {
  Import = "Import",
  YouTube = "YouTube",
  Vimeo = "Vimeo"
}

export interface CouponContentSettings {
  CouponUrl: string;
  CouponName: string;
  CouponId: string;
}

export interface TemplateExtendedProperties {
  LottieJson?: string;
  CouponName?: string;
  ShortLinkUrl?: string;
  LocationId?: string;
}

export interface TemplateBasePreviewOptions {
  digitalAsset?: DigitalAssetGetDto;
  json?: string;
  screenSizesJsonAsset?: ScreenSizesJsonAsset;
  mediaContent?: MediaContentDto;
}

export interface DisabledStateInfo {
  tooltipText: string;
}

export interface TemplatePreviewOptions extends TemplateBasePreviewOptions {
  templateContentType: TemplateContentType;
  templateId?: number;
  templateContentTypeId?: number;
  customContentTemplateType?: CustomContentTemplateType;
  generatedBody?: string;
  previewTitle?: string;
  previewIcon?: string;
  contentSettings?: ContentSettings;
  defaultParameters?: KeyValueAsset;
  isScreenSizeResponsive?: boolean;
  parameters?: HtmlTemplateParameters;
  templateProperties?: TemplateProperties;
  configurationOptions?: TemplateConfigurationOptionsDto;
  globalTemplateType?: GlobalTemplateType;
  disabledInfo?: DisabledStateInfo;
}

export interface QrCodeContentSettings {
  backgroundColor?: string;
  backgroundImage?: string;
  logoUrl?: string;
  imageUrl?: string;
  qrCodePrimaryColor?: string;
  qrCodeSecondaryColor?: string;
  qrCodeBackground?: string;
  qrCodeHasFrame?: boolean;
  qrCodeLogo?: string;
  qrCodeLogoType?: DisplayQrCodeLogoTypes;
  qrCodeUrl?: string;
  buttonText?: string;
  buttonWidth?: number;
  buttonTextColor?: string;
  buttonBackgroundColor?: string;
  qrActionText?: string;
  qrActionColor?: string;
  subtitleText?: string;
  subtitleColor?: string;
  titleText?: string;
  titleColor?: string;
}

export interface TemplateDestinationDto {
  destinationType: TemplateDestinationType;
  separateUrl?: boolean;
  url?: string;
  widgetType?: number;
  externalCouponId?: string;
}

export interface TemplateCustomDestinationDto {
  useDefaultDestination: boolean;
  customDestination?: TemplateDestinationDto;
}

export interface CfsTemplateMediaConfig {
  autoplay?: boolean;
  muted?: boolean;
  loop?: boolean;
  fullscreen?: boolean;
  coverParent?: boolean;
}

export interface CfsTemplateSkipInfo {
  templateId: number;
  templateName: string;
  errorGroups?: CfsTemplateErrorGroup[];
}

export interface CfsTemplateErrorGroup {
  error: CfsTemplateError;
  occurrences: CfsTemplateErrorOccurrence[];
}

export interface CfsTemplateErrorOccurrence {
  date: string;
  isActualError: boolean;
  slideIndex?: number;
}

export interface CfsTemplateError extends CfsTemplateErrorDto {
  date?: string;
  data: CfsTemplateErrorData | null;
}

export interface CfsTemplateErrorDto {
  message: string;
  type?: string;
}

export interface CfsTemplateErrorData {
  field?: string;
  value?: string;
}

export interface CfsTemplateBaseComponent {
  ready: EventEmitter<void>;
  error: EventEmitter<CfsTemplateError>;
  play(): void;
  stop(): void;
  pause(): void;
  restart(): void;
}

export type TemplateProperties = ContentSettings | UrlContentSettings | CouponContentSettings | TemplateExtendedProperties | null;

export type ScreenSizesJsonAsset = {
  [size in DigitalAssetScreenSize]?: string;
};

export enum TemplateContentSource {
  PresetImage = "PresetImage",
  Coupon = "Coupon",
  UploadedVideo = "UploadedVideo",
  Vimeo = "Vimeo",
  Youtube = "Youtube",
  CustomImage = "CustomImage",
  Text = "Text"
}

export enum DisplayQrCodeLogoTypes {
  None = 0,
  BrandLogo = 1,
  CustomLogo = 2,
  DisplayQrCodeLogo = 3
}

export enum AssetParamKeys {
  MainBackgroundColor = "%%MAIN_BG_COLOR%%",
  ButtonBackgroundColor = "%%BUTTON_BG_COLOR%%",
  MainBackgroundColorHex = "%%MAIN_BG_COLOR_HEX%%",
  ButtonBackgroundColorHex = "%%BUTTON_BG_COLOR_HEX%%",
  Param1 = "%%PARAM_1%%",
  Param2 = "%%PARAM_2%%",
  Param3 = "%%PARAM_3%%",
  Param4 = "%%PARAM_4%%",
  Param5 = "%%PARAM_5%%",
  Param6 = "%%PARAM_6%%"
}

export enum ContentSettingKeys {
  BackgroundColor = "backgroundColor",
  ButtonColor = "buttonColor",
  CampaignSchedule = "campaignSchedule",
  TemplateCategory = "templateCategory"
}

export enum CfsContentAsset {
  MainBackgroundColor = "%%MAIN_BG_COLOR%%",
  ButtonBackgroundColor = "%%BUTTON_BG_COLOR%%",
  MainBackgroundColorHex = "%%MAIN_BG_COLOR_HEX%%",
  ButtonBackgroundColorHex = "%%BUTTON_BG_COLOR_HEX%%",
  Param1 = "%%PARAM_1%%",
  Param2 = "%%PARAM_2%%",
  Param3 = "%%PARAM_3%%",
  Param4 = "%%PARAM_4%%",
  Param5 = "%%PARAM_5%%",
  Param6 = "%%PARAM_6%%"
}

export enum CfsQrCodeContentAsset {
  backgroundColor = "%%BACKGROUND_COLOR%%",
  backgroundImage = "%%BACKGROUND_IMAGE%%",
  logoUrl = "%%LOGO_URL%%",
  imageUrl = "%%IMAGE_URL%%",
  qrCodePrimaryColor = "%%QR_CODE_PRIMARY_COLOR%%",
  qrCodeSecondaryColor = "%%QR_CODE_SECONDARY_COLOR%%",
  qrCodeBackground = "%%QR_CODE_BACKGROUND%%",
  qrCodeHasFrame = "%%QR_CODE_HAS_FRAME%%",
  qrCodeLogo = "%%QR_CODE_LOGO%%",
  qrCodeLogoType = "%%QR_CODE_LOGO_TYPE%%",
  qrCodeUrl = "%%QR_CODE_URL%%",
  buttonText = "%%BUTTON_TEXT%%",
  buttonWidth = '"%%BUTTON_WIDTH%%"',
  buttonTextColor = '"%%BUTTON_TEXT_COLOR%%"',
  buttonBackgroundColor = '"%%BUTTON_BACKGROUND_COLOR%%"',
  qrActionText = "%%QR_ACTION_TEXT%%",
  qrActionColor = '"%%QR_ACTION_COLOR%%"',
  subtitleText = "%%SUBTITLE_TEXT%%",
  subtitleColor = '"%%SUBTITLE_COLOR%%"',
  titleText = "%%TITLE_TEXT%%",
  titleColor = '"%%TITLE_COLOR%%"'
}

export enum CustomContentTemplateType {
  Image = "Image",
  Video = "Video"
}

export enum TemplateAnimationType {
  Static = "Static",
  Animated = "Animated",
  OnDemandAnimated = "OnDemandAnimated"
}

export enum CFSTemplatePreviewType {
  Lottie = "lottie",
  Video = "video",
  Image = "image",
  Html = "htmlTemplate",
  GeneratedHtml = "generatedHtml",
  CmcQrCode = "cmcQrCode"
}

export type ResponsiveAssetsInfo = {
  [screenSize in DigitalAssetScreenSize]?: DigitalAssetGetDto | null;
};
